import { lazy, Suspense, useMemo, useState } from "react";
import { IconButton } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { columnIconName } from "@/lib/columnIcon";
import { StatusPill } from "@/components/ui/StatusPill";
import { DetailField } from "@/components/board/DetailField";
import { DetailColumnEditor } from "@/components/board/DetailColumnEditor";
import { MemoOriginBreadcrumb } from "@/components/board/MemoOriginBreadcrumb";
import { RowActionBar } from "@/components/board/RowActionBar";
import { RowSubtasks } from "@/components/board/RowSubtasks";
import { ShareRowDialog } from "@/components/board/ShareRowDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { usePersonalWorkspaceId } from "@/hooks/useWorkspaceMeta";

// Lazy — shares the TipTap + react-markdown chunk with the desktop panel.
const RowComments = lazy(() =>
  import("@/components/board/RowComments").then((m) => ({ default: m.RowComments })),
);
import { useAuth } from "@/hooks/useAuth";
import { useRowSubscription } from "@/hooks/useRowSubscription";
import { useMarkRowSeen } from "@/hooks/useMarkRowSeen";
import { useRowMentionPeople } from "@/hooks/useRowMentionPeople";
import { useRowShares } from "@/hooks/useRowShares";
import { rowMentionItems } from "@/lib/boardPeople";
import type { MentionItem } from "@/lib/types";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import { BRAND } from "@/lib/tokens";
import type { CellValue, UUID } from "@/lib/types";
import { isTicketBoard, type ViewBoard, type ViewRow } from "@/lib/boardView";
import { TicketLinksPanel } from "@/components/board/TicketLinksPanel";

interface RowDetailSheetProps {
  row: ViewRow;
  /** The board as the mobile list sees it: filtered rows, hidden columns dropped. */
  board: ViewBoard;
  /**
   * 🔴 The UNFILTERED board, for the Unteraufgaben section only. `board` above is the mobile
   * list's DISPLAY copy — filters have removed rows and hidden columns are gone. Derive the
   * sub-tasks from that and an active filter silently hides a child, while hiding the Status
   * column silently removes the checkbox. Falls back to `board` when the parent supplies none.
   */
  fullBoard?: ViewBoard;
  onClose: () => void;
  onPatch: (patch: Partial<ViewRow>) => void;
  onPatchCell?: (rowId: string, columnId: string, value: CellValue) => void;
  /** Unteraufgaben: mint a child row and write its title + assignee in one step. */
  onCreateSubtask?: (parentRowId: UUID, title: string, assigneeId: string | null) => void;
  /** Unteraufgaben: open a sub-task's own detail ("öffnen"). */
  onOpenRow?: (rowId: string) => void;
  /** Delete this row. Provided only for non-guests (write access) — omit to hide the control. */
  onDelete?: () => void;
  /** Jump-to-comment (①): the comment to scroll to + flash inside the thread. */
  focusCommentId?: string;
  focusCommentQuery?: string;
}

export function RowDetailSheet({
  row,
  board,
  fullBoard,
  onClose,
  onPatchCell,
  onCreateSubtask,
  onOpenRow,
  onDelete,
  focusCommentId,
  focusCommentQuery,
}: RowDetailSheetProps) {
  const sub = useRowSubscription(row.raw?.id as UUID | undefined);
  useMarkRowSeen(row.raw?.id as UUID | undefined);
  const { user } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const viewerIsCreator =
    Boolean(user?.sub) &&
    row.raw?.created_by_keycloak_sub != null &&
    user?.sub === row.raw.created_by_keycloak_sub;
  const personalWsId = usePersonalWorkspaceId();
  const canShare =
    board.workspace_id !== personalWsId && (board.access !== "guest" || viewerIsCreator);
  const rowShares = useRowShares(row.raw?.id as UUID | undefined, canShare && Boolean(row.raw));
  const shareTargets = [
    ...rowShares.shares.users.map((u) => u.display_name ?? u.username ?? u.email),
    ...rowShares.shares.departments.map((d) => d.name),
  ];
  const statusOpt = board.columns
    .find((c) => c.type === "status")
    ?.options?.find((o) => o.id === row.status);
  // Title column = header; every other column (incl. long_text under its real
  // name) renders as an editable field. No special-cased Notizen block.
  const titleColId = board.columns.find((c) => c.type === "text")?.id;
  const fieldColumns = board.columns.filter((c) => c.id !== titleColId);
  // Row-scoped @-mention pool (board members ∪ creator ∪ this row's shared-in people) —
  // see RowDetailPanel / useRowMentionPeople.
  const { data: mentionPeople = [] } = useRowMentionPeople(row.raw?.id as UUID | undefined);
  const mentionItems = useMemo<MentionItem[]>(
    () => rowMentionItems(mentionPeople),
    [mentionPeople],
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 50,
        display: "flex",
        flexDirection: "column",
        animation: "flow-sheet-in .22s ease-out",
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(13, 62, 99, 0.32)",
        }}
      ></div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--surface)",
          borderRadius: "20px 20px 0 0",
          maxHeight: "88%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 -10px 30px rgba(13,62,99,0.2)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 999,
              background: "var(--scale-300)",
            }}
          ></div>
        </div>

        <div
          style={{ padding: "4px 18px 12px", display: "flex", alignItems: "flex-start", gap: 10 }}
        >
          {statusOpt ? <StatusPill option={statusOpt} size="sm" /> : null}
          <div style={{ flex: 1 }}></div>
          {row.raw ? (
            <IconButton
              icon="bell"
              size={30}
              title={sub.subscribed ? t.unfollowAria : t.followAria}
              color={sub.subscribed ? BRAND.orange : undefined}
              disabled={sub.busy}
              onClick={() => sub.toggle.mutate(!sub.subscribed)}
            />
          ) : null}
          {row.raw && canShare ? (
            <IconButton icon="share" size={30} title={t.share} onClick={() => setShareOpen(true)} />
          ) : null}
          {onDelete ? (
            <IconButton
              icon="trash"
              size={30}
              title={t.delete}
              color="var(--danger)"
              onClick={() => setConfirmDelete(true)}
            />
          ) : null}
          <IconButton
            icon="dotsV"
            size={30}
            title={t.comingSoon}
            onClick={() => showToast(t.comingSoon)}
          />
          <IconButton icon="x" size={30} onClick={onClose} />
        </div>

        <div style={{ overflow: "auto", padding: "0 18px 28px" }}>
          <h2
            style={{
              margin: "4px 0 18px",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: "var(--ink-1)",
              lineHeight: 1.25,
            }}
          >
            {row.title}
          </h2>

          <MemoOriginBreadcrumb board={board} row={row} />

          {shareTargets.length > 0 ? (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              title={t.share}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                marginBottom: 16,
                padding: "9px 11px",
                background: "var(--surface-soft)",
                border: "1px solid var(--hairline-soft)",
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              <Icon name="share" size={14} color="var(--accent)" />
              <span style={{ fontSize: 12, color: "var(--ink-3)", flexShrink: 0 }}>
                {t.sharedWithLabel}
              </span>
              <span
                style={{
                  fontSize: 12.5,
                  color: "var(--ink-1)",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {shareTargets.join(", ")}
              </span>
            </button>
          ) : null}

          {fieldColumns.length > 0 ? (
            <div
              style={{
                background: "var(--surface-soft)",
                borderRadius: 12,
                border: "1px solid var(--hairline-soft)",
              }}
            >
              {fieldColumns.map((col, i) => (
                <DetailField
                  key={col.id}
                  icon={columnIconName(col.type)}
                  label={col.name}
                  last={i === fieldColumns.length - 1}
                >
                  {/* Keyed on the ROW so a field's local edit state never carries over to
                      another ticket (see RowDetailPanel). */}
                  <DetailColumnEditor
                    key={row.id}
                    column={col}
                    row={row}
                    board={board}
                    onPatchCell={onPatchCell}
                    mentionItems={mentionItems}
                  />
                </DetailField>
              ))}
            </div>
          ) : null}

          {/* Unteraufgaben — the SAME component the desktop panel renders (cross-surface rule:
              a control that exists only on the desktop panel does not exist on a phone). */}
          <RowSubtasks
            board={fullBoard ?? board}
            row={row}
            onPatchCell={onPatchCell}
            onCreateSubtask={onCreateSubtask}
            onOpenRow={onOpenRow}
          />

          {row.raw?.id ? (
            <>
              {isTicketBoard(board) ? <TicketLinksPanel row={row} board={board} /> : null}
              <RowActionBar row={row} board={board} />
              <div style={{ marginTop: 18 }}>
                <Suspense fallback={<div style={{ minHeight: 80 }} />}>
                  <RowComments
                    // Remount per row so an unsent draft doesn't follow into the next row.
                    key={row.raw.id as UUID}
                    rowId={row.raw.id as UUID}
                    mentionItems={mentionItems}
                    isTicket={isTicketBoard(board)}
                    focusCommentId={focusCommentId}
                    focusCommentQuery={focusCommentQuery}
                  />
                </Suspense>
              </div>
            </>
          ) : null}
        </div>
      </div>
      {row.raw && shareOpen ? (
        <ShareRowDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          rowId={row.raw.id as UUID}
          homeWorkspaceId={board.workspace_id as UUID}
        />
      ) : null}
      {onDelete ? (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title={t.rowDeleteTitle}
          message={t.rowDeleteMessage}
          confirmLabel={t.delete}
          tone="danger"
          onConfirm={() => {
            setConfirmDelete(false);
            onDelete();
          }}
        />
      ) : null}
    </div>
  );
}
